import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";
import type { TRPC_ERROR_CODE_NUMBER } from "@trpc/server/rpc";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { ButtonGroup } from "@replo/design-system/components/button/ButtonGroup";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import {
  FieldContent,
  FieldDescription,
  FieldTitle,
} from "@replo/design-system/components/field/Field";
import { Input } from "@replo/design-system/components/input/Input";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { TRPC_ERROR_CODES_BY_KEY } from "@trpc/server/rpc";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { userRouter } from "@v2/trpc/routes/user";
import { ChevronDown, X } from "lucide-react";
import { BsPlus, BsSearch } from "react-icons/bs";
import { useLocation, useNavigate } from "react-router-dom";
import { pluralize } from "replo-utils/lib/pluralize";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";

// Note (Noah, 2026-02-03): Hootsuite SDK 4.1 type definition, subset of what's exposed at https://assets.hootsuite.com/jsapi/4-1/hsp.js
declare global {
  interface Window {
    hsp?: {
      init: (options: { callback?: () => void; useTheme?: boolean }) => void;
      composeMessageV2: (
        message: string,
        params?: { scheduleTimestamp?: number },
      ) => void;
    };
  }
}

/**
 * Hootsuite Content Source integration layout.
 *
 * This component is embedded directly within Hootsuite's content source panel.
 * It loads the Hootsuite SDK dynamically and displays a list of published Replo
 * URLs that users can click to compose social media posts.
 *
 * Integration flow:
 * 1. Component loads and dynamically injects the Hootsuite SDK script
 * 2. SDK initializes and sets isHootsuiteReady = true
 * 3. User clicks a URL card and selects "Add to Post" from the menu
 * 4. Component calls hsp.composeMessageV2() to compose the message
 *
 * @author Replo 2026-01-27
 */
const HootsuiteContentSourceLayout: React.FC = () => {
  const [search, debouncedSearch, setSearch] = useDebouncedState("");
  const [selectedWorkspaceId, setSelectedWorkspaceId] = React.useState<
    string | undefined
  >(undefined);
  const [selectedProjectId, setSelectedProjectId] = React.useState<
    string | undefined
  >(undefined);
  const [isHootsuiteReady, setIsHootsuiteReady] = React.useState(false);
  const [hspLogs, setHspLogs] = React.useState<string[]>([]);
  const isDebugMode = useIsDebugMode();

  // Fetch workspaces and projects for filter dropdowns
  const {
    data: workspacesData,
    isLoading: isLoadingWorkspaces,
    error: workspacesError,
  } = userRouter.listWorkspacesAndProjects.useQuery();

  // Fetch published URLs
  const {
    data: urlsData,
    isLoading: isLoadingUrls,
    error: urlsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = userRouter.listAllPublishedUrls.useInfiniteQuery(
    {
      workspaceId: selectedWorkspaceId,
      projectId: selectedProjectId,
      search: debouncedSearch,
      limit: 20,
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      retry: false,
    },
  );

  // Get available projects based on selected workspace
  const availableProjects = React.useMemo(() => {
    if (!workspacesData?.workspaces) {
      return [];
    }
    if (selectedWorkspaceId) {
      const workspace = workspacesData.workspaces.find(
        (w) => w.id === selectedWorkspaceId,
      );
      return workspace?.projects ?? [];
    }
    return workspacesData.workspaces.flatMap((w) => w.projects);
  }, [workspacesData, selectedWorkspaceId]);

  // Flatten paginated URLs
  const allUrls = React.useMemo(() => {
    return urlsData?.pages.flatMap((page) => page.urls) ?? [];
  }, [urlsData]);

  React.useEffect(() => {
    const HOOTSUITE_SDK_URL = "https://assets.hootsuite.com/jsapi/4-1/hsp.js";
    const SCRIPT_ID = "hootsuite-sdk-script";

    const addLog = (message: string) => {
      const timestamp = new Date().toISOString();
      setHspLogs((prev) => [...prev, `[${timestamp}] ${message}`]);
    };

    // Check if script is already loaded
    if (document.getElementById(SCRIPT_ID)) {
      addLog("Script already exists in DOM");
      if (window.hsp) {
        addLog("window.hsp already available");
        setIsHootsuiteReady(true);
      }
      return;
    }

    addLog("Creating script element");
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = HOOTSUITE_SDK_URL;
    script.async = true;

    script.addEventListener("load", () => {
      addLog("Script loaded");
      if (window.hsp) {
        addLog("Initializing hsp.init() with callback");
        window.hsp.init({
          callback: () => {
            addLog("hsp.init() callback fired - SDK ready");
            setIsHootsuiteReady(true);
          },
        });
      } else {
        addLog("ERROR: Script loaded but window.hsp is undefined");
      }
    });

    script.addEventListener("error", () => {
      addLog("ERROR: Failed to load Hootsuite SDK script");
    });

    document.head.append(script);
    addLog("Script appended to document.head");

    return () => {
      const existingScript = document.getElementById(SCRIPT_ID);
      if (existingScript) {
        existingScript.remove();
        addLog("Script removed from DOM on unmount");
      }
    };
  }, []);

  const handleAddToPost = React.useCallback(
    (url: string, elementName: string) => {
      const message = `${elementName}: ${url}`;

      if (window.hsp?.composeMessageV2) {
        window.hsp.composeMessageV2(message);
      } else {
        // Fallback: copy to clipboard if SDK not available
        void navigator.clipboard.writeText(message);
      }
    },
    [],
  );

  // Handle authentication errors (401/403)
  const isAuthError =
    (workspacesError &&
      (
        [
          TRPC_ERROR_CODES_BY_KEY.FORBIDDEN,
          TRPC_ERROR_CODES_BY_KEY.UNAUTHORIZED,
        ] as (TRPC_ERROR_CODE_NUMBER | undefined)[]
      ).includes(workspacesError.shape?.code)) ||
    (urlsError &&
      (
        [
          TRPC_ERROR_CODES_BY_KEY.FORBIDDEN,
          TRPC_ERROR_CODES_BY_KEY.UNAUTHORIZED,
        ] as (TRPC_ERROR_CODE_NUMBER | undefined)[]
      ).includes(urlsError.shape?.code));

  if (isAuthError) {
    return <AuthErrorModal />;
  }

  const totalCount = urlsData?.pages[0]?.totalCount ?? 0;

  return (
    <div className="relative flex flex-col h-screen bg-surface text-default">
      {isDebugMode && (
        <HspDebugCard isHootsuiteReady={isHootsuiteReady} logs={hspLogs} />
      )}
      <div className="p-4 border-b border-border space-y-3">
        <Input
          placeholder="Search Replo pages..."
          value={search}
          size="base"
          startEnhancer={<BsSearch className="text-muted h-3 w-3" />}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="flex gap-2">
          <FilterCombobox
            placeholder="All Workspaces"
            items={
              workspacesData?.workspaces.map((workspace) => ({
                label: workspace.name,
                value: workspace.id,
              })) ?? []
            }
            selectedValue={selectedWorkspaceId}
            isDisabled={isLoadingWorkspaces}
            onSelect={(value) => {
              setSelectedWorkspaceId(value);
              setSelectedProjectId(undefined);
            }}
            onClear={() => {
              setSelectedWorkspaceId(undefined);
              setSelectedProjectId(undefined);
            }}
          />

          <FilterCombobox
            placeholder="All Projects"
            items={availableProjects.map((project) => ({
              label: project.name ?? "Untitled Project",
              value: project.id,
            }))}
            selectedValue={selectedProjectId}
            isDisabled={isLoadingWorkspaces || availableProjects.length === 0}
            onSelect={setSelectedProjectId}
            onClear={() => setSelectedProjectId(undefined)}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <UrlList
          isLoading={isLoadingUrls && allUrls.length === 0}
          urls={allUrls}
          totalCount={totalCount}
          search={debouncedSearch}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isHootsuiteReady={isHootsuiteReady}
          onAddToPost={handleAddToPost}
          onLoadMore={() => void fetchNextPage()}
        />
      </div>
    </div>
  );
};

/**
 * Reusable filter combobox with clearable selection for Hootsuite filter dropdowns.
 */
const FilterCombobox: React.FC<{
  placeholder: string;
  items: ComboboxItemWithValue[];
  selectedValue: string | undefined;
  isDisabled: boolean;
  onSelect: (value: string) => void;
  onClear: () => void;
}> = ({ placeholder, items, selectedValue, isDisabled, onSelect, onClear }) => {
  const selectedItem = items.find((item) => item.value === selectedValue);
  return (
    <ButtonGroup className="flex-1">
      <Combobox
        placeholder="Search..."
        items={items}
        value={selectedItem ?? null}
        onValueChange={(item) => {
          if (item) {
            onSelect(item.value);
          }
        }}
        trigger={
          <Button
            variant="secondary"
            size="base"
            disabled={isDisabled}
            UNSAFE_className="flex-1"
            endEnhancer={<ChevronDown className="size-3 opacity-50" />}
          >
            {selectedItem?.label ?? placeholder}
          </Button>
        }
        triggerAsChild
      />
      {selectedValue && (
        <Button
          variant="secondary"
          size="base"
          icon
          onClick={onClear}
          aria-label="Clear selection"
        >
          <X className="size-3" />
        </Button>
      )}
    </ButtonGroup>
  );
};

type PublishedUrl = {
  elementId: string;
  elementName: string;
  publishedUrl: string;
  workspaceName: string;
  projectName: string | null;
};

/**
 * List of published URLs with loading and empty states.
 * Each URL card has a click menu with "Add to Post" option.
 */
const UrlList: React.FC<{
  isLoading: boolean;
  urls: PublishedUrl[];
  totalCount: number;
  search: string;
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  isHootsuiteReady: boolean;
  onAddToPost: (url: string, elementName: string) => void;
  onLoadMore: () => void;
}> = ({
  isLoading,
  urls,
  totalCount,
  search,
  hasNextPage,
  isFetchingNextPage,
  isHootsuiteReady,
  onAddToPost,
  onLoadMore,
}) => {
  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        {[...new Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (urls.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center text-muted">
        <p className="text-base font-medium">No published pages found</p>
        <p className="text-sm mt-1">
          {search
            ? "Try adjusting your search or filters"
            : "Publish some pages in Replo to see them here"}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="px-4 py-2 text-xs text-muted border-b border-border">
        {totalCount} published{" "}
        {pluralize({ singular: "page", plural: "pages", count: totalCount })}
      </div>
      <div className="flex flex-col gap-2 px-4 py-2">
        {urls.map((url) => (
          <Menu key={url.elementId}>
            <MenuTrigger
              render={
                <div className="border-0.5 border-border flex w-full cursor-pointer rounded-lg bg-white hover:bg-sheet">
                  <div className="flex flex-1 flex-col gap-4 p-3">
                    <FieldContent>
                      <FieldTitle>{url.elementName}</FieldTitle>
                      <FieldDescription size="sm">
                        {url.publishedUrl}
                      </FieldDescription>
                    </FieldContent>
                    <div className="flex flex-1 items-center justify-center">
                      <div className="text-xs text-muted w-full text-left">
                        {url.workspaceName} / {url.projectName ?? "Untitled"}
                      </div>
                    </div>
                  </div>
                </div>
              }
            />
            <MenuContent>
              <MenuItem
                label="Add to Post"
                startEnhancer={<BsPlus className="h-4 w-4" />}
                disabled={!isHootsuiteReady}
                onClick={() => {
                  onAddToPost(url.publishedUrl, url.elementName);
                }}
              />
            </MenuContent>
          </Menu>
        ))}
      </div>

      {/* Load more button */}
      {hasNextPage && (
        <div className="p-4">
          <button
            type="button"
            onClick={() => onLoadMore()}
            disabled={isFetchingNextPage}
            className="w-full py-2 text-sm text-primary hover:underline disabled:opacity-50"
          >
            {isFetchingNextPage ? "Loading..." : "Load more"}
          </button>
        </div>
      )}
    </>
  );
};

/**
 * Debug card showing HSP (Hootsuite SDK) status and logs.
 * Only visible when useIsDebugMode returns true.
 */
const HspDebugCard: React.FC<{
  isHootsuiteReady: boolean;
  logs: string[];
}> = ({ isHootsuiteReady, logs }) => {
  return (
    <div className="absolute top-2 right-2 z-50 w-80 max-h-64 overflow-auto bg-black text-white text-xs rounded-md shadow-lg p-3">
      <div className="font-semibold mb-2">HSP Debug Info</div>
      <div className="flex items-center gap-2 mb-2">
        <span>Status:</span>
        <span
          className={`px-2 py-0.5 rounded ${
            isHootsuiteReady ? "bg-emerald text-white" : "bg-amber text-white"
          }`}
        >
          {isHootsuiteReady ? "Ready" : "Not Ready"}
        </span>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <span>window.hsp:</span>
        <span className="font-mono">
          {typeof window !== "undefined" && window.hsp
            ? "Available"
            : "Undefined"}
        </span>
      </div>
      <div className="border-t border-border pt-2 mt-2">
        <div className="font-semibold mb-1">Logs:</div>
        {logs.length === 0 ? (
          <div className="text-muted italic">No logs yet</div>
        ) : (
          <div className="space-y-1 font-mono text-[10px]">
            {logs.map((log, index) => (
              <div key={index} className="break-all">
                {log}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Auth error modal shown when the user is not authenticated (401/403 error).
 * Redirects to the standard login page with a ?next param so the user is
 * returned to the Hootsuite integration after authenticating.
 */
const AuthErrorModal: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleLoginClick = () => {
    navigate(`/auth/login?next=${location.pathname}`);
  };

  return (
    <div id="app" className="bg-surface h-screen w-full">
      <Modal
        open={true}
        onOpenChange={() => {}}
        title="Authentication Required"
        description="Please log in to your Replo account to access your published pages."
        size="sm"
        dismissable={false}
        footer={
          <Button
            variant="primary"
            size="base"
            className="w-full"
            onClick={handleLoginClick}
          >
            Log in to Replo
          </Button>
        }
      >
        <p className="text-xs">
          You need to be logged in to your Replo account to use this
          integration. Click the button below to log in. You will be redirected
          back here after logging in.
        </p>
      </Modal>
    </div>
  );
};

export default HootsuiteContentSourceLayout;
